import { BotonExportar } from '@/components/dominio/BotonExportar';
import { ComentarioSeccion } from '@/components/dominio/ComentarioSeccion';
import { KpiCard } from '@/components/dominio/KpiCard';
import { MontoCRC } from '@/components/dominio/MontoCRC';
import { SelectorPeriodo } from '@/components/dominio/SelectorPeriodo';
import { SortableHeader, useSortColumn } from '@/components/dominio/SortableHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePeriodo } from '@/hooks/usePeriodo';
import { useDetalle } from '@/hooks/useUtilidades';
import { cliente } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type { DatosDetalle } from '@adelante/shared';
import { formatearPeriodo, mesAnclaPeriodo, obtenerRangoPeriodo } from '@adelante/shared';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Fragment, useMemo, useRef, useState } from 'react';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

// porPagina alto que se usa SOLO en exportaciones (PDF / Excel) para traer
// todos los movimientos del mes en una sola llamada. El backend acepta hasta 10000.
const POR_PAGINA_EXPORTAR = 10000;

const NOMBRES_COMPONENTES: Record<string, string> = {
  inversion_casas: 'Inv. Casas',
  inversion_proyectos: 'Inv. Proyectos',
  otros: 'Otros',
  salida_quinta: 'Quinta',
  salida_homes: 'Homes',
  salida_socios: 'Socios',
  credito_clientes: 'Créd. Clientes',
  credito_colaboradores: 'Créd. Colabs.',
  compra_maquinaria: 'Maquinaria',
};

const COLORES_COMPONENTES = [
  '#2563eb',
  '#3b82f6',
  '#6366f1',
  '#8b5cf6',
  '#a855f7',
  '#06b6d4',
  '#0891b2',
  '#0284c7',
  '#0369a1',
];

function abreviarCRC(valor: number): string {
  if (Math.abs(valor) >= 1e9) return `₡${(valor / 1e9).toFixed(1)}bn`;
  if (Math.abs(valor) >= 1e6) return `₡${(valor / 1e6).toFixed(0)}M`;
  return `₡${valor.toLocaleString('es-CR')}`;
}

type FilaMovDetalle = {
  id_utilidad_mov: number;
  fecha: Date;
  lote: string | null;
  tipo_movimiento: string;
  tg_suma_resta: boolean;
  detalle_transferencia: string | null;
  monto_colones: number;
};

type GrupoMovimiento = {
  lote: string;
  movimientos: FilaMovDetalle[];
  sumaTotal: number;
};

interface RenderSeccionArgs {
  seccion: 'ing' | 'egr';
  label: string;
  grupos: GrupoMovimiento[];
  subtotal: number;
  expandidos: Set<string>;
  onToggle: (clave: string) => void;
  esEgreso: boolean;
}

function renderSeccion(args: RenderSeccionArgs) {
  const { seccion, label, grupos, subtotal, expandidos, onToggle, esEgreso } = args;
  if (grupos.length === 0) return null;

  const colorHeader = esEgreso
    ? 'bg-destructive/10 text-destructive'
    : 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300';

  // En la TD (no en el TR) lleva `data-pdf-page-break-before` para que en el
  // PDF arranque siempre en una hoja nueva. Está en TD porque las TRs tienen
  // layout especial y getBoundingClientRect a veces devuelve posiciones poco
  // confiables — las TDs tienen posicionamiento sólido como cualquier celda.
  const cellProps: Record<string, string> = esEgreso ? { 'data-pdf-page-break-before': '' } : {};

  return (
    <Fragment key={seccion}>
      <TableRow className={cn('hover:bg-transparent', colorHeader)}>
        <TableCell
          {...cellProps}
          colSpan={4}
          className="py-2 text-xs font-bold uppercase tracking-wider"
        >
          {label}
        </TableCell>
        <TableCell className="py-2 text-right text-sm font-semibold">
          Subtotal: <MontoCRC monto={subtotal} />
        </TableCell>
      </TableRow>

      {grupos.map((grupo) => {
        const movs = grupo.movimientos;
        const tieneMultiples = movs.length > 1;
        const clave = `${seccion}:${grupo.lote}`;
        const estaExpandido = expandidos.has(clave);

        if (!tieneMultiples) {
          const mov = movs[0]!;
          return (
            <TableRow key={mov.id_utilidad_mov}>
              <TableCell />
              <TableCell className="font-medium">{mov.lote ?? '—'}</TableCell>
              <TableCell>{mov.tipo_movimiento}</TableCell>
              <TableCell className="max-w-[200px] truncate text-muted-foreground">
                {mov.detalle_transferencia ?? '—'}
              </TableCell>
              <TableCell className={cn('text-right', esEgreso && 'text-destructive')}>
                <MontoCRC monto={Number(mov.monto_colones)} />
              </TableCell>
            </TableRow>
          );
        }

        return (
          <Fragment key={clave}>
            <TableRow
              className="cursor-pointer select-none hover:bg-muted/40"
              onClick={() => onToggle(clave)}
            >
              <TableCell className="text-muted-foreground">
                {estaExpandido ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </TableCell>
              <TableCell className="font-semibold">{grupo.lote}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {movs.length} movimientos
              </TableCell>
              <TableCell />
              <TableCell className={cn('text-right font-semibold', esEgreso && 'text-destructive')}>
                <MontoCRC monto={grupo.sumaTotal} />
              </TableCell>
            </TableRow>

            {estaExpandido &&
              movs.map((mov) => (
                <TableRow key={mov.id_utilidad_mov} className="bg-muted/20">
                  <TableCell className="text-center text-xs text-muted-foreground">↳</TableCell>
                  <TableCell className="pl-4 text-sm text-muted-foreground">
                    {new Date(mov.fecha).toLocaleDateString('es-CR')}
                  </TableCell>
                  <TableCell className="text-sm">{mov.tipo_movimiento}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                    {mov.detalle_transferencia ?? '—'}
                  </TableCell>
                  <TableCell className={cn('text-right text-sm', esEgreso && 'text-destructive')}>
                    <MontoCRC monto={Number(mov.monto_colones)} />
                  </TableCell>
                </TableRow>
              ))}
          </Fragment>
        );
      })}
    </Fragment>
  );
}

export function DetalleUtilidadPantalla() {
  const { periodo, setModo, setAnio, setMes, setTrimestre, setDesde, setHasta } = usePeriodo();
  const refContenido = useRef<HTMLDivElement>(null);
  const [pagina, setPagina] = useState(1);
  const [filtroLote, setFiltroLote] = useState('');
  const [filtroTexto, setFiltroTexto] = useState('');
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());

  // Estado especial: cuando estamos exportando, sustituimos la data paginada
  // por la versión completa (todos los movimientos del mes). Se restaura a
  // null al terminar la exportación para volver a la vista paginada normal.
  const [dataExportar, setDataExportar] = useState<DatosDetalle | null>(null);

  const etiquetaPeriodo = formatearPeriodo(periodo);
  const ancla = mesAnclaPeriodo(periodo);

  const queryClient = useQueryClient();
  const { data: dataPaginada, isLoading, error } = useDetalle(periodo, pagina);

  // Data efectiva que usa el render: cuando hay export en curso usamos la
  // completa, si no la paginada. Así un mismo conjunto de useMemo recalcula
  // grupos/totales automáticamente al alternar.
  const data = dataExportar ?? dataPaginada;

  const sortGrupo = useSortColumn<GrupoMovimiento>(null);

  // Resetear paginación y expansión cuando el usuario cambia cualquier dim del período.
  // Wrappers que envuelven cada setter del hook.
  const resetVista = () => {
    setPagina(1);
    setExpandidos(new Set());
  };
  const onSetModo = (m: typeof periodo.modo) => {
    setModo(m);
    resetVista();
  };
  const onSetAnio = (a: number) => {
    setAnio(a);
    resetVista();
  };
  const onSetMes = (m: number) => {
    setMes(m);
    resetVista();
  };
  const onSetTrimestre = (q: 1 | 2 | 3 | 4) => {
    setTrimestre(q);
    resetVista();
  };
  const onSetDesde = (a: number, m: number) => {
    setDesde(a, m);
    resetVista();
  };
  const onSetHasta = (a: number, m: number) => {
    setHasta(a, m);
    resetVista();
  };

  function toggleLote(clave: string) {
    setExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(clave)) next.delete(clave);
      else next.add(clave);
      return next;
    });
  }

  // Para no colisionar cuando un mismo lote aparece en ambas secciones
  function claveLote(seccion: 'ing' | 'egr', lote: string): string {
    return `${seccion}:${lote}`;
  }

  // Total de páginas calculado contra la data paginada (no la de export).
  // Cuando data === dataExportar, totalPaginas será 1 y el paginador queda oculto.
  const totalPaginas = dataPaginada
    ? Math.ceil(dataPaginada.paginacion.total / dataPaginada.paginacion.porPagina)
    : 0;

  const datosComponentes = data
    ? Object.entries(data.componentesGastada)
        .map(([k, v]) => ({ nombre: NOMBRES_COMPONENTES[k] ?? k, monto: v }))
        .filter((d) => d.monto > 0)
        .sort((a, b) => b.monto - a.monto)
    : [];

  // Agrupar movimientos por lote, separados por sección (Ingresos vs Egresos).
  // tg_suma_resta === true → ingreso (suma); false → egreso (resta).
  // Filtros (filtroLote, filtroTexto) se aplican dentro de cada sección.
  const seccionesGrupos = useMemo<{
    ingresos: GrupoMovimiento[];
    egresos: GrupoMovimiento[];
    totalIngresos: number;
    totalEgresos: number;
  }>(() => {
    if (!data) {
      return { ingresos: [], egresos: [], totalIngresos: 0, totalEgresos: 0 };
    }

    const construirGrupos = (movs: FilaMovDetalle[]): GrupoMovimiento[] => {
      const map = new Map<string, FilaMovDetalle[]>();
      for (const mov of movs) {
        const lote = mov.lote ?? '(sin lote)';
        if (!map.has(lote)) map.set(lote, []);
        map.get(lote)!.push(mov);
      }
      let arr: GrupoMovimiento[] = Array.from(map.entries()).map(([lote, m]) => ({
        lote,
        movimientos: m,
        sumaTotal: m.reduce((acc, x) => acc + Number(x.monto_colones), 0),
      }));
      if (filtroLote.trim()) {
        const t = filtroLote.trim().toLowerCase();
        arr = arr.filter((g) => g.lote.toLowerCase().includes(t));
      }
      if (filtroTexto.trim()) {
        const t = filtroTexto.trim().toLowerCase();
        arr = arr.filter(
          (g) =>
            g.lote.toLowerCase().includes(t) ||
            g.movimientos.some(
              (m) =>
                m.tipo_movimiento.toLowerCase().includes(t) ||
                (m.detalle_transferencia ?? '').toLowerCase().includes(t),
            ),
        );
      }
      return arr;
    };

    const ingresos = construirGrupos(
      data.movimientos.filter((m) => m.tg_suma_resta) as FilaMovDetalle[],
    );
    const egresos = construirGrupos(
      data.movimientos.filter((m) => !m.tg_suma_resta) as FilaMovDetalle[],
    );
    return {
      ingresos,
      egresos,
      totalIngresos: ingresos.reduce((acc, g) => acc + g.sumaTotal, 0),
      totalEgresos: egresos.reduce((acc, g) => acc + g.sumaTotal, 0),
    };
  }, [data, filtroLote, filtroTexto]);

  const ingresosOrdenados = useMemo(
    () => sortGrupo.sortData(seccionesGrupos.ingresos),
    [seccionesGrupos.ingresos, sortGrupo.sortData],
  );
  const egresosOrdenados = useMemo(
    () => sortGrupo.sortData(seccionesGrupos.egresos),
    [seccionesGrupos.egresos, sortGrupo.sortData],
  );

  // Hooks de exportación (PDF y Excel):
  // - `antesDeExportar` / `despuesDeExportar` corren para AMBOS PDF y Excel:
  //   disparan el fetch completo (porPagina alto) y guardan en `dataExportar`
  //   para que tanto el render (capturado por html2canvas para PDF) como las
  //   tablas planas (consumidas por exportarExcel) usen los 95+ registros
  //   en vez de solo los 50 de la página actual.
  // - `antesDeExportarPDF` / `despuesDeExportarPDF` corren SOLO para PDF y
  //   se encargan de la expansión de filas agrupadas — Excel no la necesita.
  //
  // BotonExportar garantiza con doble rAF que tras `antesDeExportar` el
  // padre haya re-renderizado y la prop `tablas` esté actualizada antes
  // de leerla para Excel.
  //
  // Las claves de expansión incluyen sufijo de sección porque un mismo lote
  // puede aparecer en Ingresos y Egresos simultáneamente.
  const expandidosPreviosRef = useRef<Set<string> | null>(null);
  const dataCompletaRef = useRef<DatosDetalle | null>(null);

  async function antesDeExportar() {
    // Fetch imperativo de la data completa via react-query cache
    const rango = obtenerRangoPeriodo(periodo);
    const respuesta = await queryClient.fetchQuery({
      queryKey: [
        'utilidades-detalle-exportar',
        rango.desdeAnio,
        rango.desdeMes,
        rango.hastaAnio,
        rango.hastaMes,
        POR_PAGINA_EXPORTAR,
      ],
      queryFn: async () => {
        const res = await cliente.utilidades.detalle({
          desdeAnio: rango.desdeAnio,
          desdeMes: rango.desdeMes,
          hastaAnio: rango.hastaAnio,
          hastaMes: rango.hastaMes,
          pagina: 1,
          porPagina: POR_PAGINA_EXPORTAR,
        });
        if (!res.data) throw new Error(res.error?.mensaje ?? 'Error cargando movimientos');
        return res.data;
      },
      staleTime: 60_000, // si exporta varias veces seguidas, reusa la cache
    });
    dataCompletaRef.current = respuesta;
    setDataExportar(respuesta);
  }

  function despuesDeExportar() {
    setDataExportar(null);
    dataCompletaRef.current = null;
  }

  function antesDeExportarPDF() {
    // Calcular expansión desde la data completa (no desde los useMemo
    // que aún no se recalcularon con el setState recién hecho)
    const respuesta = dataCompletaRef.current;
    if (!respuesta) return;
    expandidosPreviosRef.current = expandidos;
    const conteoIng = new Map<string, number>();
    const conteoEgr = new Map<string, number>();
    for (const m of respuesta.movimientos) {
      const lote = m.lote ?? '(sin lote)';
      const target = m.tg_suma_resta ? conteoIng : conteoEgr;
      target.set(lote, (target.get(lote) ?? 0) + 1);
    }
    const todos = new Set<string>();
    for (const [lote, count] of conteoIng) {
      if (count > 1) todos.add(claveLote('ing', lote));
    }
    for (const [lote, count] of conteoEgr) {
      if (count > 1) todos.add(claveLote('egr', lote));
    }
    setExpandidos(todos);
  }

  function despuesDeExportarPDF() {
    if (expandidosPreviosRef.current) {
      setExpandidos(expandidosPreviosRef.current);
      expandidosPreviosRef.current = null;
    }
  }

  return (
    <div ref={refContenido} className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Detalle de Movimientos</h1>
          <p className="mt-1 text-sm text-muted-foreground">{etiquetaPeriodo}</p>
        </div>
        <div data-pdf-ignore className="flex flex-wrap items-start gap-3">
          <SelectorPeriodo
            periodo={periodo}
            onSetModo={onSetModo}
            onSetAnio={onSetAnio}
            onSetMes={onSetMes}
            onSetTrimestre={onSetTrimestre}
            onSetDesde={onSetDesde}
            onSetHasta={onSetHasta}
          />
          <BotonExportar
            titulo="Detalle de Movimientos"
            subtitulo={etiquetaPeriodo}
            refContenido={refContenido}
            disabled={!data}
            onAntesExportar={antesDeExportar}
            onDespuesExportar={despuesDeExportar}
            onAntesExportarPDF={antesDeExportarPDF}
            onDespuesExportarPDF={despuesDeExportarPDF}
            tablas={
              data
                ? [
                    {
                      nombre: 'Ingresos',
                      encabezados: ['Lote', 'Fecha', 'Tipo', 'Detalle', 'Monto (₡)'],
                      filas: data.movimientos
                        .filter((m) => m.tg_suma_resta)
                        .map((m) => [
                          m.lote ?? '(sin lote)',
                          new Date(m.fecha).toLocaleDateString('es-CR'),
                          m.tipo_movimiento,
                          m.detalle_transferencia ?? '',
                          Number(m.monto_colones),
                        ]),
                    },
                    {
                      nombre: 'Egresos',
                      encabezados: ['Lote', 'Fecha', 'Tipo', 'Detalle', 'Monto (₡)'],
                      filas: data.movimientos
                        .filter((m) => !m.tg_suma_resta)
                        .map((m) => [
                          m.lote ?? '(sin lote)',
                          new Date(m.fecha).toLocaleDateString('es-CR'),
                          m.tipo_movimiento,
                          m.detalle_transferencia ?? '',
                          Number(m.monto_colones),
                        ]),
                    },
                  ]
                : []
            }
          />
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Cargando…
        </div>
      )}

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6 text-destructive">{error.message}</CardContent>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
            <KpiCard titulo="Utilidad Ingresada" monto={data.ecuacionNeta.utilidad_ingresada} />
            <KpiCard titulo="Devolución Utilidad" monto={data.ecuacionNeta.devolucion_utilidad} />
            <KpiCard titulo="Total Utilidad" monto={data.ecuacionNeta.utilidad_total} />
            <KpiCard titulo="Gastada" monto={data.ecuacionNeta.utilidad_gastada} />
            <KpiCard titulo="Neta" monto={data.ecuacionNeta.utilidad_neta} destacado />
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Componentes de utilidad gastada</CardTitle>
            </CardHeader>
            <CardContent>
              {datosComponentes.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart
                    data={datosComponentes}
                    layout="vertical"
                    margin={{ left: 8, right: 32, top: 4, bottom: 4 }}
                  >
                    <XAxis
                      type="number"
                      tickFormatter={abreviarCRC}
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="nombre"
                      width={100}
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip
                      formatter={(v) => [abreviarCRC(Number(v)), 'Monto']}
                      cursor={{ fill: 'hsl(var(--muted))' }}
                    />
                    <Bar dataKey="monto" radius={[0, 4, 4, 0]}>
                      {datosComponentes.map((d, i) => (
                        <Cell
                          key={d.nombre}
                          fill={COLORES_COMPONENTES[i % COLORES_COMPONENTES.length]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">Sin datos</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="text-sm">Movimientos</CardTitle>
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Filtrar lote…"
                    className="h-8 w-[130px]"
                    value={filtroLote}
                    onChange={(e) => setFiltroLote(e.target.value)}
                  />
                  <Input
                    placeholder="Buscar tipo, detalle…"
                    className="h-8 w-[190px]"
                    value={filtroTexto}
                    onChange={(e) => setFiltroTexto(e.target.value)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-6" />
                    <TableHead>
                      <SortableHeader
                        label="Lote"
                        sortKey="lote"
                        activeSortKey={sortGrupo.sortKey}
                        direction={sortGrupo.direction}
                        onSort={sortGrupo.handleSort}
                      />
                    </TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Detalle</TableHead>
                    <TableHead className="text-right">
                      <SortableHeader
                        label="Monto"
                        sortKey="sumaTotal"
                        activeSortKey={sortGrupo.sortKey}
                        direction={sortGrupo.direction}
                        onSort={sortGrupo.handleSort}
                        align="right"
                      />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {renderSeccion({
                    seccion: 'ing',
                    label: 'INGRESOS',
                    grupos: ingresosOrdenados,
                    subtotal: seccionesGrupos.totalIngresos,
                    expandidos,
                    onToggle: toggleLote,
                    esEgreso: false,
                  })}
                  {renderSeccion({
                    seccion: 'egr',
                    label: 'EGRESOS',
                    grupos: egresosOrdenados,
                    subtotal: seccionesGrupos.totalEgresos,
                    expandidos,
                    onToggle: toggleLote,
                    esEgreso: true,
                  })}

                  {ingresosOrdenados.length === 0 && egresosOrdenados.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        Sin movimientos
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {totalPaginas > 1 && (
                <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                  <span>
                    Página {pagina} de {totalPaginas} ({data.paginacion.total} registros)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagina <= 1}
                      onClick={() => setPagina((p) => p - 1)}
                    >
                      Anterior
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={pagina >= totalPaginas}
                      onClick={() => setPagina((p) => p + 1)}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <ComentarioSeccion anio={ancla.anio} mes={ancla.mes} scope="seccion" seccionId="detalle" />
    </div>
  );
}
